import { ACSCandidateProps } from "@/types";
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { getAllOnboardedCandidateData } from "@/lib/actions/acs.actions";
import CandidateLists from "./CandidateLists";

const AdminSideBar = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedCandidate, setSelectedCandidate] =
    useState<ACSCandidateProps | null>(null);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [allCandidates, setAllCandidates] = useState<ACSCandidateProps[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [count, setCount] = useState<number>(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const fetchPage = async (pageNumber: number) => {
    if (isLoadingMore || pageNumber < 1) return;

    setIsLoadingMore(true);
    try {
      const response = await getAllOnboardedCandidateData(pageNumber);

      if (response?.results) {
        setAllCandidates(response.results); // Replace instead of append for prev/next
        setPage(pageNumber);
        setHasNextPage(!!response.next);
      }
    } catch (error) {
      console.error("Error loading candidates", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const totalPages = Math.ceil(count / 50);

  useEffect(() => {
    const fetchInitialCandidates = async () => {
      try {
        setIsInitialLoading(true);
        const response = await getAllOnboardedCandidateData(1);
        if (response?.results) {
          setAllCandidates(response.results);
          setHasNextPage(!!response.next);
          setCount(response.count);
        }
      } catch (error) {
        console.error("Failed to load initial candidates:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchInitialCandidates();
  }, []);

  useEffect(() => {
    if (!allCandidates.length) return;

    const candidate = id
      ? allCandidates.find((c) => c.id == id)
      : allCandidates[0];

    setSelectedCandidate(candidate || null);
  }, [allCandidates, id]);

  const profileCreatedCandidates = useMemo(() => {
    return allCandidates.filter((candidate) => candidate.has_profile);
  }, [allCandidates]);

  const recommendedCandidates = useMemo(() => {
    return allCandidates.filter((candidate) => candidate.recommended && !candidate.has_profile);
  }, [allCandidates]);

  return (
    <aside className="w-72 bg-gray h-screen p-4 fixed left-0 top-28 overflow-y-auto">
      {isInitialLoading && (
        <div className="w-72 bg-gray h-screen p-4 fixed left-0 top-28 overflow-y-auto flex flex-col items-center justify-center gap-3 z-50">
          <Loader2 className="animate-spin text-red" size={32} />
          <p className="text-sm text-gray-700 font-medium">Loading Candidates...</p>
        </div>
      )}
      {/* Recommended Candidates */}
      <CandidateLists
        title="Recommended Candidates"
        candidates={recommendedCandidates}
        selectedCandidate={selectedCandidate}
        onSelect={setSelectedCandidate}
        type="admin"
      />
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center px-2">
          <button
            onClick={() => fetchPage(page - 1)}
            disabled={page === 1 || isLoadingMore}
            className="p-1.5 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-md disabled:opacity-50 transition-colors"
            title="Previous Page"
          >
            <ChevronLeft size={18} />
          </button>
          
          <div className="flex items-center gap-2">
            {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin text-red" />}
            <p className="text-xs text-gray-600 font-medium">
              Page {page} of {totalPages}
            </p>
          </div>
          
          <button
            onClick={() => fetchPage(page + 1)}
            disabled={!hasNextPage || isLoadingMore}
            className="p-1.5 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-md disabled:opacity-50 transition-colors"
            title="Next Page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
      {/* Profile Created */}
      <div className="my-5">
        <CandidateLists
          title="Profile Created"
          candidates={profileCreatedCandidates}
          selectedCandidate={selectedCandidate}
          onSelect={setSelectedCandidate}
          showCheckmark={true}
          type="admin"
          created={true}
        />
      </div>
    </aside>
  );
};

export default AdminSideBar;
